import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/authStore';
import type { AssetNotification } from '@/services/notificationsService';
import { resolveNotificationTarget, type NotificationTarget } from '@/lib/notificationTarget';

/**
 * Click behaviour for a notification, shared by the Notifications page and
 * the Header dropdown. `targetOf` decides whether to offer "View details";
 * `open` navigates, or explains why it can't.
 */
export function useOpenNotification() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  // Subscribed so a permissions refresh re-resolves targets.
  const permissions = useAuthStore((s) => s.permissions);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const viewer = useMemo(
    () => ({
      isPlatformAdmin: user?.roles.includes('platform_admin') ?? false,
      hasPermission,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, permissions, hasPermission],
  );

  const targetOf = useCallback(
    (n: AssetNotification): NotificationTarget => resolveNotificationTarget(n, viewer),
    [viewer],
  );

  const open = useCallback(
    (n: AssetNotification): NotificationTarget => {
      const target = targetOf(n);
      if (target?.kind === 'link') navigate(target.to);
      else if (target?.kind === 'denied') toast.error('You no longer have access to this item');
      return target;
    },
    [navigate, targetOf],
  );

  return { targetOf, open };
}
